import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  OffDeviceRefundMethod,
  OffDeviceRefundResult,
  PosTransaction,
  posService
} from '@/data/posService';
import { deriveOffDeviceRefundLegs } from '@/utils/posRefund';

interface Props {
  /** The Token / InPOS sale to record; `null` keeps the dialog closed. */
  transaction: PosTransaction | null;
  onClose: () => void;
  /** Called with the server answer when the record was accepted. */
  onRecorded: (result: OffDeviceRefundResult) => void;
}

const REASON_MIN = 8;
const REASON_MAX = 500;
const REFERENCE_MAX = 100;

/**
 * "Cihaz dışında iade edildi": Token (Beko) and InPOS cannot refund a paid
 * sale through their API (a paid Beko fiş cannot even be voided on the
 * device). The branch hands the money back outside the device and records it
 * here; the server books the FULL amount and reverses the sale on our side in
 * one step. There is no amount field on purpose.
 */
const OffDevicePosRefundDialog = ({
  transaction,
  onClose,
  onRecorded
}: Props) => {
  const { t } = useTranslation();
  const [methods, setMethods] = useState<OffDeviceRefundMethod[]>([]);
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [deviceVoidPrinted, setDeviceVoidPrinted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const legs = transaction ? deriveOffDeviceRefundLegs(transaction) : null;
  const adapterKey = transaction?.provider?.adapterKey;
  const isInpos = adapterKey === 'INPOS';

  useEffect(() => {
    if (!transaction) return;
    setMethods(
      (deriveOffDeviceRefundLegs(transaction) ?? []).map(
        (leg) => leg.defaultMethod
      )
    );
    setReason('');
    setReference('');
    setDeviceVoidPrinted(false);
    setError(null);
  }, [transaction]);

  const trimmedReason = reason.trim();
  const reasonValid =
    trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;
  const canSubmit = !!legs && reasonValid && !submitting;

  const close = () => {
    if (submitting) return;
    onClose();
  };

  const submit = async () => {
    if (!transaction || !legs || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await posService.recordOffDeviceRefund(transaction.id, {
        reason: trimmedReason,
        legs: legs.map((leg, index) => ({
          type: leg.type,
          method: methods[index] ?? leg.defaultMethod
        })),
        externalReference: reference.trim() || undefined,
        ...(isInpos ? { deviceVoidPrinted } : {})
      });
      onRecorded(result);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ?? t('posTransactions.offDevice.error')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!transaction} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('posTransactions.offDevice.title', { id: transaction?.id })}
      </DialogTitle>
      <DialogContent>
        {transaction && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {adapterKey && (
              <Alert severity="warning">
                {t(`posTransactions.offDevice.warning.${adapterKey}`)}
              </Alert>
            )}
            <Typography fontWeight="bold">
              {t('posTransactions.offDevice.amount', {
                amount: Number(transaction.amount).toFixed(2),
                currency: transaction.currency
              })}
            </Typography>
            {!legs ? (
              <Alert severity="error">
                {t('posTransactions.offDevice.unknownTender')}
              </Alert>
            ) : (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">
                  {t('posTransactions.offDevice.legs')}
                </Typography>
                {legs.map((leg, index) => (
                  <FormControl
                    key={`${leg.type}-${index}`}
                    size="small"
                    fullWidth
                  >
                    <InputLabel id={`off-device-leg-${index}`}>
                      {`${t(`posTransactions.offDevice.leg.${leg.type}`)} · ${leg.amount.toFixed(2)} ${transaction.currency}`}
                    </InputLabel>
                    <Select
                      labelId={`off-device-leg-${index}`}
                      label={`${t(`posTransactions.offDevice.leg.${leg.type}`)} · ${leg.amount.toFixed(2)} ${transaction.currency}`}
                      value={methods[index] ?? leg.defaultMethod}
                      disabled={leg.methods.length === 1}
                      onChange={(event) =>
                        setMethods((prev) => {
                          const next = [...prev];
                          next[index] = event.target
                            .value as OffDeviceRefundMethod;
                          return next;
                        })
                      }
                    >
                      {leg.methods.map((method) => (
                        <MenuItem key={method} value={method}>
                          {t(`posTransactions.offDevice.method.${method}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Stack>
            )}
            <TextField
              label={t('posTransactions.offDevice.reason')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required
              fullWidth
              multiline
              minRows={2}
              inputProps={{ maxLength: REASON_MAX }}
              error={reason.length > 0 && !reasonValid}
              helperText={t('posTransactions.offDevice.reasonHelp')}
            />
            <TextField
              label={t('posTransactions.offDevice.reference')}
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              fullWidth
              inputProps={{ maxLength: REFERENCE_MAX }}
            />
            {isInpos && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deviceVoidPrinted}
                    onChange={(event) =>
                      setDeviceVoidPrinted(event.target.checked)
                    }
                  />
                }
                label={t('posTransactions.offDevice.deviceVoidPrinted')}
              />
            )}
            <Typography variant="body2" color="text.secondary">
              {t('posTransactions.offDevice.effect')}
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={submitting}>
          {t('cancel')}
        </Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit}>
          {t('posTransactions.offDevice.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OffDevicePosRefundDialog;
